import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Dropdown, InputProps } from 'tea-component';
import { getStartAndEndValue } from '../../utils';
import { LocaleType } from '@/pages/opinion/types/common';
import type { PlaceholderType } from '../../types';
import classNames from 'classnames';
import { addFilterBuriedPoint } from '../../utils/point';
import { AllFilterPointType } from '@op/operationLog/pages/filter';
import CustomInput from '../CustomInput';
import '../scss/range.scss';
import useLanguage from '@op/hooks/useLanguage';

function CustomInputDropdown(
  {
    lang,
    rangeValue,
    setRangeValue,
    placeholder,
    unit = 'K',
    unitPosition = 'suffix',
    maxValue,
    minValue,
    maxErrorText,
    minErrorText,
    errorTextFormatted = false,
    regex = /^\d+$/,
    emptyErrorText,
    valueErrorText,
    backText = "Cancel",
    pointKey,
    getBackPoint,
    className,
    inputProps,
    showClear
  }: {
    /** 当前语言环境 */
    lang: LocaleType;
    /** 当前选中的范围值 */
    rangeValue: string;
    /** 设置范围值的回调 */
    setRangeValue: (value: string) => void;
    /** 各语言下的占位符文本 */
    placeholder: PlaceholderType;
    /** 单位符号（如 K、M），默认 'K' */
    unit?: string;
    /** 单位位置：'suffix' 后缀（默认） | 'prefix' 前缀 */
    unitPosition?: 'prefix' | 'suffix';
    /** 最大值限制 */
    maxValue?: number;
    /** 最小值限制 */
    minValue?: number;
    /** 超过最大值时的自定义错误提示文案 */
    maxErrorText?: string;
    /** 低于最小值时的自定义错误提示文案 */
    minErrorText?: string;
    /** 标记 maxErrorText / minErrorText 是否已经过 formatMessage 处理，默认 false */
    errorTextFormatted?: boolean;
    /** 输入值格式校验正则，默认 /^\d+(\.\d{1,3})?$/ */
    regex?: RegExp;
    /** 空输入自定义错误提示文案 */
    emptyErrorText?: string;
    /** 格式错误自定义错误提示文案 */
    valueErrorText?: string;
    /** 返回按钮文案，支持 intl key，默认 'Back' */
    backText?: string;
    /** 埋点 key，用于筛选器确认时上报 */
    pointKey?: AllFilterPointType;
    /** 自定义埋点值提取函数，接收 rangeValue 返回埋点值 */
    getBackPoint?: (value: string) => string;
    /** 自定义类名 */
    className?: string;
    inputProps?: InputProps & React.RefAttributes<HTMLInputElement>;
    showClear?: boolean;
  },
  ref: React.Ref<unknown> | undefined,
) {
  const { formatMessage } = useLanguage();

  // 将 rangeValue 字符串（如 "15-100"）解析为 { text: "15K ~ 100K", value: "15-100" } 格式
  const parseRangeValue = (
    rawValue: string,
  ): { text: string; value: string } | null => {
    if (!rawValue) return null;
    const parts = rawValue.split('-');
    if (parts.length !== 2) return null;
    const [startStr, endStr] = parts;
    if (!startStr || !endStr) return null;
    const [start, end] = getStartAndEndValue(startStr, endStr);
    const formatValue = (v: string | number) =>
      unitPosition === 'prefix' ? `${unit}${v}` : `${v}${unit}`;
    return {
      text: `${formatValue(start)} ~ ${formatValue(end)}`,
      value: `${start}-${end}`,
    };
  };

  const [customValue, setCustomValue] = useState<{
    text: string;
    value: string;
  } | null>(() => parseRangeValue(rangeValue));

  // 当外部 rangeValue prop 变化时（如父组件重置筛选、URL 参数恢复等），同步 customValue
  useEffect(() => {
    setCustomValue(parseRangeValue(rangeValue));
  }, [rangeValue]);

  const getRangeButton = (value: string) => {
    if (!value || !customValue) return formatMessage({ id: 'All' });
    return customValue.text;
  };

  useImperativeHandle(ref, () => ({
    buttonElement: (value: string, isPoint?: boolean) => {
      if (isPoint) return customValue?.value || value;
      if (!value || !customValue) return formatMessage({ id: 'All' });
      return customValue.text;
    },
  }));

  const handleCustomConfirm =
    (close: () => void) => (startRange: string, endRange: string) => {
      // 两边都没有输入 → 全部
      if (!startRange && !endRange) {
        setCustomValue(null);
        setRangeValue('');
        close();
        return;
      }
      const [start, end] = getStartAndEndValue(startRange, endRange);
      const formatValue = (v: string | number) =>
        unitPosition === 'prefix' ? `${unit}${v}` : `${v}${unit}`;
      const newRange = {
        text: `${formatValue(start)} ~ ${formatValue(end)}`,
        value: `${start}-${end}`,
      };
      setCustomValue(newRange);
      setRangeValue(newRange.value);
      close();
    }; 
  return (
    <div className={classNames('follower-range-wrapper', className)}>
      <Dropdown
        className="dbo-followerfilter"
        boxClassName={`dbo-followerfilter${className || ''}`}
        button={
          <div className="dbo-followerfilter-inner">
            <span className="select-button-text">{placeholder[lang]}</span>
            <span className="dbo-followerfilter-value">
              {getRangeButton(rangeValue)}
            </span>
          </div>
        }
        size="m"
        appearance="button"
        matchButtonWidth
        clickClose={false}
        onClose={() => {
          try {
            const backValue = customValue?.value || rangeValue;
            const pointValue = getBackPoint ? getBackPoint(rangeValue) : backValue;
            addFilterBuriedPoint(pointKey, pointValue);
          } catch (error) {}
        }}
      >
        {(close) => (
          <CustomInput
            unit={unit}
            unitPosition={unitPosition}
            maxValue={maxValue}
            minValue={minValue}
            maxErrorText={maxErrorText}
            minErrorText={minErrorText}
            errorTextFormatted={errorTextFormatted}
            regex={regex}
            emptyErrorText={emptyErrorText}
            valueErrorText={valueErrorText}
            backText={backText}
            rangeValue={rangeValue}
            onConfirm={handleCustomConfirm(close)}
            onBack={close}
            inputProps={inputProps}
            showClear={showClear}
          />
        )}
      </Dropdown>
    </div>
  );
}

export default forwardRef(CustomInputDropdown);
